import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { ArrowSquareOut, Info, Lightning, MagnifyingGlass, Warning } from 'shared/icons';
import PageHeader from '../components/layout/PageHeader';
import { MlValue } from '../components/mlExplorer/MlValue';
import { PriceSuggestionItems, PriceSuggestionSummary } from '../components/mlExplorer/PriceSuggestionPanels';
import { Mono } from '../components/ui/Mono';
import api from '../services/api';
import { DescribeMlValue, IsMlNotOwnerResponse, MlScalarText, MlValueText } from '../utils/mlPayloadValues';

/**
 * Explorador de Endpoints do Mercado Livre.
 *
 * POR QUE ESTA TELA EXISTE: o Melidash guarda um ESPELHO do que o Mercado Livre
 * devolve, já filtrado pelos schemas de validação. Quando a API ganha um campo
 * novo — como os quatro de desconto automático — não dá para saber pelo banco
 * se ele está chegando, porque o que o schema não declara é descartado antes de
 * gravar. Aqui a resposta vem CRUA, exatamente como o ML mandou.
 *
 * A tela não escreve nada: só GET, e só nos endpoints do catálogo do backend.
 */

/** Os quatro campos de boost. Ganham destaque próprio no painel de achados. */
const BOOST_FIELDS = new Set([
    'boosted_offer',
    'discount_meli_boosted_percentage',
    'discount_meli_boost_amount',
    'total_price_for_boosted_offer',
]);

/** Endpoint aberto por padrão: é onde a doc coloca os campos de boost. */
const DEFAULT_ENDPOINT_ID = 'seller_promotions_item';

/** Referências de preços: os dois endpoints que ganham resumo próprio acima do JSON. */
const PRICE_SUGGESTION_ITEMS_ID = 'price_suggestions_user_items';
const PRICE_SUGGESTION_DETAIL_ID = 'price_suggestions_item_details';

const isSellerParam = (param) => param.autofill === 'seller_id';

/**
 * Endpoint que não tem nada para o usuário preencher: todo parâmetro
 * obrigatório é o nosso vendedor. Esse já consulta ao ser escolhido (dono,
 * 22/10) — sem o vendedor no ambiente não, porque o servidor recusaria.
 */
function runsOnSelect(endpoint, sellerId) {
    if (!endpoint) {
        return false;
    }
    const params = endpoint.params || [];
    if (params.some((param) => param.required && !isSellerParam(param))) {
        return false;
    }
    return !params.some(isSellerParam) || Boolean(sellerId);
}

function StatusPill({ status }) {
    const ok = status >= 200 && status < 300;
    const tone = ok
        ? 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200'
        : 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200';
    return <span className={`rounded px-2 py-0.5 text-xs font-semibold ${tone}`}>HTTP {status}</span>;
}

const MlExplorer = () => {
    const [catalog, setCatalog] = useState(null);
    const [catalogError, setCatalogError] = useState(null);
    const [endpointId, setEndpointId] = useState(DEFAULT_ENDPOINT_ID);
    // Cada clique num endpoint é uma seleção nova — inclusive no mesmo endpoint.
    // A consulta automática roda uma vez por seleção, nunca a cada render.
    const [selection, setSelection] = useState(0);
    const [values, setValues] = useState({});
    const [result, setResult] = useState(null);
    const [error, setError] = useState(null);
    const [loading, setLoading] = useState(false);
    // Só a resposta da ÚLTIMA consulta entra na tela: trocar de endpoint com
    // uma consulta no ar não pode deixar o resultado do anterior aparecer.
    const requestSeqRef = useRef(0);
    const autoRunSelectionRef = useRef(null);

    useEffect(() => {
        let alive = true;
        api.getMlExplorerCatalog()
            .then((data) => {
                if (!alive) return;
                setCatalog(data);
                setCatalogError(null);
            })
            .catch((err) => {
                if (!alive) return;
                setCatalogError(err?.message || 'Não foi possível carregar o catálogo de endpoints.');
            });
        return () => {
            alive = false;
        };
    }, []);

    // `|| []` cru criaria um array novo a cada render e invalidaria os memos
    // abaixo sempre — memoizado, só muda quando o catálogo chega.
    const endpoints = useMemo(() => catalog?.endpoints || [], [catalog]);
    const selected = useMemo(() => endpoints.find((entry) => entry.id === endpointId) || null, [endpoints, endpointId]);

    const grouped = useMemo(() => {
        const map = new Map();
        for (const entry of endpoints) {
            const list = map.get(entry.group) || [];
            list.push(entry);
            map.set(entry.group, list);
        }
        return [...map.entries()];
    }, [endpoints]);

    const runRequest = useCallback(async (targetId, params) => {
        requestSeqRef.current += 1;
        const seq = requestSeqRef.current;
        setLoading(true);
        setError(null);
        try {
            const data = await api.runMlExplorerRequest(targetId, params);
            if (seq === requestSeqRef.current) {
                setResult(data);
            }
        } catch (err) {
            if (seq === requestSeqRef.current) {
                setError(err?.message || 'A consulta falhou.');
                setResult(null);
            }
        } finally {
            if (seq === requestSeqRef.current) {
                setLoading(false);
            }
        }
    }, []);

    const resetFor = useCallback((nextId, nextValues) => {
        requestSeqRef.current += 1;
        setEndpointId(nextId);
        setSelection((previous) => previous + 1);
        setValues(nextValues);
        setResult(null);
        setError(null);
        setLoading(false);
    }, []);

    const handleSelect = useCallback((nextId) => resetFor(nextId, {}), [resetFor]);

    const handleRun = useCallback(() => {
        if (!selected) {
            return;
        }
        runRequest(selected.id, values);
    }, [selected, values, runRequest]);

    // Da lista de anúncios com referência para o detalhe de um deles: o clique
    // no MLB já é o pedido de consulta.
    const handleOpenItemDetail = useCallback(
        (itemId) => {
            resetFor(PRICE_SUGGESTION_DETAIL_ID, { item_id: itemId });
            runRequest(PRICE_SUGGESTION_DETAIL_ID, { item_id: itemId });
        },
        [resetFor, runRequest],
    );

    useEffect(() => {
        if (!catalog || !selected || autoRunSelectionRef.current === selection) {
            return;
        }
        autoRunSelectionRef.current = selection;
        if (runsOnSelect(selected, catalog.seller_id)) {
            runRequest(selected.id, {});
        }
    }, [catalog, selected, selection, runRequest]);

    const boostFound = (result?.fields_found || []).filter((entry) => BOOST_FIELDS.has(entry.field));
    const otherFound = (result?.fields_found || []).filter((entry) => !BOOST_FIELDS.has(entry.field));
    const resultEndpoint = endpoints.find((entry) => entry.id === result?.endpoint_id) || null;
    const resultOk = result ? result.status >= 200 && result.status < 300 : false;
    const currency = MlScalarText(result?.payload?.currency_id) || 'BRL';
    const notOwner = result ? IsMlNotOwnerResponse(result.status, result.payload) : false;
    const showsDetailSummary = resultOk && result.endpoint_id === PRICE_SUGGESTION_DETAIL_ID;
    const showsItemsSummary = resultOk && result.endpoint_id === PRICE_SUGGESTION_ITEMS_ID;
    const noFieldsFound = boostFound.length === 0 && otherFound.length === 0;

    return (
        <div className="space-y-6">
            <PageHeader
                title="Explorador de Endpoints"
                subtitle="Consulta a API do Mercado Livre ao vivo e mostra a resposta crua, sem passar pelo espelho do banco"
            />

            {catalogError ? (
                <div className="flex items-start gap-2 rounded-xl border border-red-200 bg-red-50 p-4 text-sm text-red-800 dark:border-red-900 dark:bg-red-950 dark:text-red-200">
                    <Warning size={18} />
                    <span>{catalogError}</span>
                </div>
            ) : null}

            <div className="grid gap-6 lg:grid-cols-[320px_minmax(0,1fr)]">
                {/* ── Escolha do endpoint ─────────────────────────────────── */}
                <div className="space-y-4">
                    <div className="rounded-xl border border-mono-200 bg-white p-4 dark:border-mono-800 dark:bg-mono-950">
                        <h2 className="mb-3 text-sm font-semibold text-black dark:text-white">Endpoint</h2>
                        <div className="space-y-4">
                            {grouped.map(([group, entries]) => (
                                <div key={group}>
                                    <p className="mb-1 text-xs font-semibold uppercase tracking-wide text-mono-500 dark:text-mono-400">
                                        {group}
                                    </p>
                                    <div className="flex flex-col gap-1">
                                        {entries.map((entry) => {
                                            const isActive = entry.id === endpointId;
                                            return (
                                                <button
                                                    key={entry.id}
                                                    type="button"
                                                    onClick={() => handleSelect(entry.id)}
                                                    className={`rounded-lg px-3 py-2 text-left text-sm transition-colors ${
                                                        isActive
                                                            ? 'bg-black text-white dark:bg-white dark:text-black'
                                                            : 'text-mono-700 hover:bg-mono-100 dark:text-mono-300 dark:hover:bg-mono-900'
                                                    }`}
                                                >
                                                    {entry.label}
                                                </button>
                                            );
                                        })}
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>

                    {catalog?.seller_id ? (
                        <p className="px-1 text-xs text-mono-500 dark:text-mono-400">
                            Seller conectado: <Mono>{catalog.seller_id}</Mono>
                        </p>
                    ) : null}
                </div>

                {/* ── Parâmetros + resultado ──────────────────────────────── */}
                <div className="space-y-4">
                    {selected ? (
                        <div className="rounded-xl border border-mono-200 bg-white p-5 dark:border-mono-800 dark:bg-mono-950">
                            <div className="mb-1 flex flex-wrap items-center gap-2">
                                <h2 className="text-lg font-semibold text-black dark:text-white">{selected.label}</h2>
                                {selected.docs ? (
                                    <a
                                        href={selected.docs}
                                        target="_blank"
                                        rel="noreferrer"
                                        className="inline-flex items-center gap-1 text-xs text-blue-700 hover:underline dark:text-blue-300"
                                    >
                                        documentação <ArrowSquareOut size={12} />
                                    </a>
                                ) : null}
                            </div>
                            <p className="mb-3 text-sm text-mono-600 dark:text-mono-400">{selected.summary}</p>
                            <Mono className="mb-4 block text-xs text-mono-500 dark:text-mono-400">
                                GET {selected.path}
                            </Mono>

                            {selected.params.length > 0 ? (
                                <div className="grid gap-3 sm:grid-cols-2">
                                    {selected.params.map((param) => (
                                        <label key={param.name} className="block text-sm">
                                            <span className="mb-1 block font-medium text-mono-700 dark:text-mono-300">
                                                {param.label}
                                                {param.required ? (
                                                    <span className="ml-1 text-red-600 dark:text-red-400">*</span>
                                                ) : null}
                                            </span>
                                            {Array.isArray(param.options) ? (
                                                <select
                                                    value={values[param.name] ?? ''}
                                                    onChange={(event) =>
                                                        setValues((prev) => ({
                                                            ...prev,
                                                            [param.name]: event.target.value,
                                                        }))
                                                    }
                                                    className="w-full rounded-lg border border-mono-300 bg-white px-3 py-2 text-sm dark:border-mono-700 dark:bg-mono-900 dark:text-white"
                                                >
                                                    <option value="">
                                                        {param.required ? 'Selecione…' : '(não enviar)'}
                                                    </option>
                                                    {param.options.map((option) => (
                                                        <option key={option} value={option}>
                                                            {option}
                                                        </option>
                                                    ))}
                                                </select>
                                            ) : isSellerParam(param) ? (
                                                // Sempre o nosso vendedor: o servidor ignora qualquer outro valor.
                                                <input
                                                    type="text"
                                                    name={param.name}
                                                    value={catalog?.seller_id || ''}
                                                    placeholder="não configurado neste ambiente"
                                                    readOnly
                                                    aria-readonly="true"
                                                    className="w-full cursor-not-allowed rounded-lg border border-mono-200 bg-mono-100 px-3 py-2 font-mono text-sm text-mono-600 dark:border-mono-800 dark:bg-mono-900 dark:text-mono-400"
                                                />
                                            ) : (
                                                <input
                                                    type="text"
                                                    name={param.name}
                                                    value={values[param.name] ?? ''}
                                                    placeholder={param.example || ''}
                                                    onChange={(event) =>
                                                        setValues((prev) => ({
                                                            ...prev,
                                                            [param.name]: event.target.value,
                                                        }))
                                                    }
                                                    className="w-full rounded-lg border border-mono-300 bg-white px-3 py-2 font-mono text-sm dark:border-mono-700 dark:bg-mono-900 dark:text-white"
                                                />
                                            )}
                                            {isSellerParam(param) ? (
                                                <span className="mt-1 block text-xs text-mono-500 dark:text-mono-400">
                                                    o nosso vendedor
                                                </span>
                                            ) : null}
                                            {param.help ? (
                                                <span className="mt-1 block text-xs text-mono-500 dark:text-mono-400">
                                                    {param.help}
                                                </span>
                                            ) : null}
                                        </label>
                                    ))}
                                </div>
                            ) : (
                                <p className="text-sm text-mono-500 dark:text-mono-400">
                                    Este endpoint não recebe parâmetros.
                                </p>
                            )}

                            <button
                                type="button"
                                onClick={handleRun}
                                disabled={loading}
                                className="mt-4 inline-flex items-center gap-2 rounded-lg bg-black px-4 py-2 text-sm font-medium text-white disabled:opacity-50 dark:bg-white dark:text-black"
                            >
                                <MagnifyingGlass size={16} />
                                {loading ? 'Consultando…' : 'Consultar'}
                            </button>
                        </div>
                    ) : null}

                    {error ? (
                        <div className="flex items-start gap-2 rounded-xl border border-red-200 bg-red-50 p-4 text-sm text-red-800 dark:border-red-900 dark:bg-red-950 dark:text-red-200">
                            <Warning size={18} />
                            <span>{error}</span>
                        </div>
                    ) : null}

                    {result ? (
                        <>
                            {showsDetailSummary ? <PriceSuggestionSummary payload={result.payload} /> : null}
                            {showsItemsSummary ? (
                                <PriceSuggestionItems payload={result.payload} onOpenItem={handleOpenItemDetail} />
                            ) : null}
                            {result.endpoint_id === PRICE_SUGGESTION_DETAIL_ID && result.status === 404 ? (
                                <p className="flex items-start gap-2 rounded-xl border border-mono-200 bg-white p-4 text-sm text-mono-700 dark:border-mono-800 dark:bg-mono-950 dark:text-mono-300">
                                    <Info size={16} />
                                    Este anúncio não tem referência de preço (o ML responde 404).
                                </p>
                            ) : null}
                            {notOwner ? (
                                <p className="flex items-start gap-2 rounded-xl border border-mono-200 bg-white p-4 text-sm text-mono-700 dark:border-mono-800 dark:bg-mono-950 dark:text-mono-300">
                                    <Info size={16} />
                                    Este anúncio é de outro vendedor: o ML só responde sobre os anúncios do nosso (401).
                                </p>
                            ) : null}

                            <div className="rounded-xl border border-mono-200 bg-white p-5 dark:border-mono-800 dark:bg-mono-950">
                                <div className="mb-3 flex flex-wrap items-center gap-3">
                                    <StatusPill status={result.status} />
                                    <span className="text-xs text-mono-500 dark:text-mono-400">
                                        {result.duration_ms} ms
                                    </span>
                                </div>
                                <Mono className="block break-all text-xs text-mono-600 dark:text-mono-400">
                                    {result.request.url}
                                    {Object.keys(result.request.query || {}).length > 0
                                        ? `?${new URLSearchParams(result.request.query).toString()}`
                                        : ''}
                                </Mono>
                            </div>

                            {/* Painel de achados: o motivo da tela existir. Vazio e com resumo
                                próprio acima (lista de anúncios com referência), ele só repetiria o nada. */}
                            {noFieldsFound && showsItemsSummary ? null : (
                                <div className="rounded-xl border border-mono-200 bg-white p-5 dark:border-mono-800 dark:bg-mono-950">
                                    <h3 className="mb-3 flex items-center gap-2 text-sm font-semibold text-black dark:text-white">
                                        <Lightning size={16} />
                                        Campos observados nesta resposta
                                    </h3>

                                    {noFieldsFound ? (
                                        <p className="flex items-start gap-2 text-sm text-mono-600 dark:text-mono-400">
                                            <Info size={16} />
                                            {resultEndpoint?.group === 'Promoções' ? (
                                                <span>
                                                    Nenhum dos campos monitorados apareceu. Para os campos de boost isso
                                                    é o esperado quando a oferta não recebeu desconto automático: a
                                                    documentação diz que eles só existem quando{' '}
                                                    <Mono>boosted_offer</Mono> é verdadeiro.
                                                </span>
                                            ) : (
                                                <span>Nenhum dos campos monitorados apareceu.</span>
                                            )}
                                        </p>
                                    ) : null}

                                    {boostFound.length > 0 ? (
                                        <div className="mb-4">
                                            <p className="mb-2 text-xs font-semibold uppercase tracking-wide text-amber-700 dark:text-amber-300">
                                                Desconto automático (boost)
                                            </p>
                                            <div className="space-y-2">
                                                {boostFound.map((entry) => (
                                                    <FieldRow
                                                        key={entry.field}
                                                        entry={entry}
                                                        currency={currency}
                                                        highlight
                                                    />
                                                ))}
                                            </div>
                                        </div>
                                    ) : null}

                                    {otherFound.length > 0 ? (
                                        <div>
                                            <p className="mb-2 text-xs font-semibold uppercase tracking-wide text-mono-500 dark:text-mono-400">
                                                Outros campos monitorados
                                            </p>
                                            <div className="space-y-2">
                                                {otherFound.map((entry) => (
                                                    <FieldRow key={entry.field} entry={entry} currency={currency} />
                                                ))}
                                            </div>
                                        </div>
                                    ) : null}
                                </div>
                            )}

                            <div className="rounded-xl border border-mono-200 bg-white p-5 dark:border-mono-800 dark:bg-mono-950">
                                <h3 className="mb-3 text-sm font-semibold text-black dark:text-white">Resposta crua</h3>
                                <pre className="max-h-[32rem] overflow-auto rounded-lg bg-mono-50 p-4 font-mono text-xs leading-relaxed text-mono-800 dark:bg-mono-900 dark:text-mono-200">
                                    {JSON.stringify(result.payload, null, 2)}
                                </pre>
                            </div>
                        </>
                    ) : null}
                </div>
            </div>
        </div>
    );
};

/**
 * Um campo observado. Valor simples sai numa linha; objeto (preço, custos,
 * promotion_detail…) sai em pares chave: valor, uma amostra por bloco — antes
 * os dois casos passavam por String() e o objeto virava "[object Object]".
 */
const FieldRow = ({ entry, highlight = false, currency = 'BRL' }) => {
    const simple = entry.samples.every((sample) => sample === null || typeof sample !== 'object');
    return (
        <div
            className={`rounded-lg border p-3 ${
                highlight
                    ? 'border-amber-300 bg-amber-50 dark:border-amber-800 dark:bg-amber-950'
                    : 'border-mono-200 bg-mono-50 dark:border-mono-800 dark:bg-mono-900'
            }`}
        >
            <div className="flex flex-wrap items-center gap-2">
                <Mono className="text-sm font-medium text-black dark:text-white">{entry.field}</Mono>
                <span className="text-xs text-mono-500 dark:text-mono-400">
                    {entry.count} {entry.count === 1 ? 'ocorrência' : 'ocorrências'}
                </span>
            </div>
            {simple ? (
                <div className="mt-1 text-xs text-mono-600 dark:text-mono-400">
                    <span className="font-medium">{entry.samples.length === 1 ? 'valor:' : 'valores:'}</span>{' '}
                    <Mono>
                        {entry.samples
                            .map((sample) => MlValueText(DescribeMlValue(sample, { key: entry.field, currency })))
                            .join(', ')}
                    </Mono>
                </div>
            ) : (
                <div className="mt-1 text-xs text-mono-600 dark:text-mono-400">
                    <span className="font-medium">{entry.samples.length === 1 ? 'valor:' : 'valores:'}</span>
                    <div className="mt-1 space-y-1">
                        {entry.samples.map((sample, index) => (
                            <div
                                key={index}
                                className="rounded border border-mono-200 bg-white px-2 py-1 dark:border-mono-800 dark:bg-mono-950"
                            >
                                <MlValue value={sample} fieldKey={entry.field} currency={currency} />
                            </div>
                        ))}
                    </div>
                </div>
            )}
            <div className="mt-1 break-all text-xs text-mono-500 dark:text-mono-500">
                <span className="font-medium">onde:</span> <Mono>{entry.paths.join('  ·  ')}</Mono>
            </div>
        </div>
    );
};

export default MlExplorer;
